/* ============================================================
 * ADG Infinite — shared "load ahead of the scroll" engine.
 *
 * Used by the artwork grid, the paginated collection grid, and the
 * three Build-Your-Own builders. One implementation so a fix in one
 * place is a fix everywhere.
 *
 * ------------------------------------------------------------
 * WHY IT IS BUILT THIS WAY (learned the hard way — please read
 * before "improving" it):
 *
 * 1. NO SCROLL LISTENER. The obvious backstop — a rAF-throttled
 *    scroll handler that measures the sentinel with
 *    getBoundingClientRect() — forces a synchronous layout on every
 *    single frame. Against a grid of thousands of cards that is the
 *    single most expensive thing on the page, and it gets worse the
 *    further you scroll. It reads as "scroll, stick, chug-chug-chug".
 *    This engine never measures anything during a scroll.
 *
 * 2. NO requestIdleCallback, NO setTimeout GATE. Both delay the
 *    batch behind the shopper's thumb. Idle callbacks in particular
 *    exist to yield to interaction, which is the exact opposite of
 *    what is wanted here.
 *
 * 3. RE-ARM INSTEAD. IntersectionObserver only fires when the
 *    intersection STATE CHANGES, so if the sentinel is still in range
 *    after a batch, no second callback ever arrives and loading
 *    stalls until the shopper scrolls away and back. The fix is to
 *    unobserve/observe after each batch: observe() always queues a
 *    fresh callback carrying the CURRENT state. Costs nothing, needs
 *    no measurement, and closes the hole completely.
 *
 * 4. ONE BATCH PER CALLBACK. Re-arming is asynchronous, so the
 *    browser always gets to paint between batches. Never loop.
 *
 * Everything above behaves identically on iOS, Android, macOS,
 * Windows and Linux, because IntersectionObserver is the only moving
 * part. Browsers too old to have it (iOS < 12.2, IE) simply get the
 * caller's ordinary "Load more" button — create() returns null and
 * the caller leaves its button visible.
 * ============================================================ */
(function (window, document) {
  'use strict';

  var SUPPORTED = typeof window.IntersectionObserver === 'function';

  /**
   * create(opts) -> { check, pause, resume, destroy } | null
   *
   *   sentinel   Element  required. Empty node placed after the grid.
   *   load       Function required. Loads exactly ONE batch. May return
   *                       a Promise; the engine waits for it.
   *   progress   Function returns a number that grows as content is
   *                       added (e.g. card count). Used to detect a
   *                       load that achieved nothing.
   *   canLoad    Function return false when exhausted, busy, or over a
   *                       ceiling. Checked before every batch.
   *   onStop     Function called once when the engine gives up, so the
   *                       caller can restore its button.
   *   lookahead  Number   px above the sentinel that counts as "near".
   *   scroller   Element  nested scroll container, or null for the page.
   *
   * Returns null when the browser has no IntersectionObserver, which
   * the caller must treat as "keep the manual button visible".
   */
  var lastInputKeyboard = false;
  document.addEventListener('keydown', function (e) { if (e.key === 'Tab') lastInputKeyboard = true; }, true);
  document.addEventListener('pointerdown', function () { lastInputKeyboard = false; }, true);

  function create(opts) {
    if (!SUPPORTED || !opts || !opts.sentinel || typeof opts.load !== 'function') return null;

    var sentinel  = opts.sentinel;
    var load      = opts.load;
    var progress  = typeof opts.progress === 'function' ? opts.progress : function () { return 0; };
    var canLoad   = typeof opts.canLoad  === 'function' ? opts.canLoad  : function () { return true; };
    var onStop    = typeof opts.onStop   === 'function' ? opts.onStop   : function () {};
    var lookahead = opts.lookahead > 0 ? opts.lookahead : 800;

    function focusPastSentinel() {
      var el = document.activeElement;
      if (!lastInputKeyboard || !el || el === document.body) return false;
      return !!(sentinel.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING);
    }

    var running = false;    // a batch is in flight
    var paused  = false;
    var dead    = false;
    var stalls  = 0;        // consecutive batches that added nothing
    var io      = null;

    // A fresh observe() always queues a callback describing the CURRENT
    // state, so this both re-triggers a still-in-range sentinel and
    // quietly does nothing when the batch pushed it out of range.
    function rearm() {
      if (dead || paused || !io) return;
      io.unobserve(sentinel);
      io.observe(sentinel);
    }

    function stop() {
      if (dead) return;
      dead = true;
      if (io) { io.disconnect(); io = null; }
      teardownListeners();
    }

    function settle(before) {
      running = false;
      if (dead) return;
      if (progress() === before) {
        // Nothing arrived. Once can be a slow page; twice means we are
        // asking for something that will never come, so give the button
        // back rather than spinning.
        if (++stalls >= 2) { stop(); onStop(); return; }
      } else {
        stalls = 0;
      }
      rearm();
    }

    function onIntersect(entries) {
      if (dead || paused || running) return;
      var hit = false;
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { hit = true; break; }
      }
      if (!hit) return;
      if (!canLoad()) return;
      // Keyboard users tabbing past the grid toward the footer brought the
      // sentinel into range, and every batch pushed the footer away again
      // (WCAG 2.1.1 / 2.4.3, RC check B M2). While keyboard focus sits AFTER
      // the grid, do not auto-load; scrolling or focus back in the grid
      // resumes it.
      if (focusPastSentinel()) return;

      var before = progress();
      running = true;
      var result;
      try {
        result = load();
      } catch (err) {
        running = false;
        if (window.console) console.error('[adg-infinite] load threw', err);
        return;
      }
      if (result && typeof result.then === 'function') {
        result.then(function () { settle(before); }, function () { running = false; });
      } else {
        settle(before);
      }
    }

    // Layout can change without any scrolling: rotating the phone, the
    // iOS toolbar collapsing, a filter shrinking the grid, or coming
    // back via the bfcache with the page restored mid-list. These are
    // rare events, so re-arming on them costs nothing.
    function onLayoutChange() { rearm(); }
    function onPageShow(e) { if (e.persisted) rearm(); }

    function teardownListeners() {
      window.removeEventListener('resize', onLayoutChange);
      window.removeEventListener('orientationchange', onLayoutChange);
      window.removeEventListener('pageshow', onPageShow);
    }

    io = new IntersectionObserver(onIntersect, {
      root: opts.scroller || null,
      rootMargin: lookahead + 'px 0px'
    });
    io.observe(sentinel);

    window.addEventListener('resize', onLayoutChange, { passive: true });
    window.addEventListener('orientationchange', onLayoutChange, { passive: true });
    window.addEventListener('pageshow', onPageShow);

    return {
      // Nudge the engine after the caller changes something itself —
      // a new filter, a search, a reset.
      check: function () { stalls = 0; rearm(); },
      pause: function () { paused = true; },
      resume: function () { paused = false; rearm(); },
      destroy: stop
    };
  }

  /* ---- shared skeleton helper -------------------------------------
     Marks a card's media box as loading so it shimmers, and clears it
     on load OR error so a broken image never shimmers forever. Cached
     images fire no event, hence the naturalWidth check. */
  function skeletonise(scope, mediaSelector) {
    var nodes = scope.querySelectorAll(mediaSelector || '.adg-card__media');
    for (var i = 0; i < nodes.length; i++) {
      (function (media) {
        var img = media.querySelector('img');
        if (!img) return;
        if (img.complete && img.naturalWidth > 0) { media.classList.remove('is-loading'); return; }
        media.classList.add('is-loading');
        var settle = function () { media.classList.remove('is-loading'); };
        img.addEventListener('load', settle, { once: true });
        img.addEventListener('error', settle, { once: true });
      })(nodes[i]);
    }
  }

  window.ADGInfinite = { create: create, skeletonise: skeletonise, supported: SUPPORTED };
})(window, document);
